import React, { useState } from "react";
import { Table, Segment, Header, Button } from "semantic-ui-react";

import { useParams } from "react-router-dom";
import InspectionStatusLabel from "./InspectionStatusLabel";
import { InspectionStatus, InspectionStatuses } from "./citizen-inspection-list/constants/InspectionStatus";
import InspectionRequestModal from "./inspection-request-modal/InspectionRequestModal";
import usePublicInspections from "../../hooks/inspections/public/usePublicInspections";

import { faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InspectionScheduledDateDisplay from "./InspectionScheduledDateDisplay";

interface ICitizenInspectionList {
  permitId: string;
}
export const CitizenInspectionList = ({ permitId }: ICitizenInspectionList) => {
  const { site } = useParams<{ site: string }>();
  const { data: inspectionData } = usePublicInspections({ recordId: permitId });
  const inspectionList = inspectionData?.data;

  if (inspectionList === undefined) {
    return <></>;
  }
  const handleRowClick = (id: string) => {
    window.open(`/${site}/public/permits/inspections/${id}`);
  };

  const [requestModalInspectionId, setRequestModalInspectionId] = useState<string | null>(null);
  const requestModalOpen = requestModalInspectionId !== null;

  const onRequestModalOpen = (id: string) => {
    setRequestModalInspectionId(id);
  };
  const onRequestModalClose = () => {
    setRequestModalInspectionId(null);
  };

  return (
    <Segment className='inspection-list tw-pr-2'>
      {requestModalInspectionId && (
        <InspectionRequestModal
          inspectionId={requestModalInspectionId}
          isOpen={requestModalOpen}
          onClose={onRequestModalClose}
        />
      )}
      <Header as='h3' content='Inspections' />
      <Table basic size='large' unstackable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Inspection</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell textAlign='right'>Scheduled Date</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {inspectionList?.map((inspection) => {
            const {
              title,
              status,
              scheduledAt: scheduledAt,
              request,
              previousInspectionId,
              scheduledDate: scheduledDate,
              scheduledMeridiem: scheduledMeridiem,
            } = inspection;
            const preferences = request.preferences ?? [];
            return (
              <Table.Row
                key={inspection.id}
                className='clickable-table-row'
                positive={status == "passed"}
                negative={status == "failed"}
                onClick={() => handleRowClick(inspection.id)}
              >
                <Table.Cell>
                  <InspectionTitleCell title={title} isReInspection={!!previousInspectionId} />
                </Table.Cell>
                <Table.Cell className='capitalize'>
                  <InspectionStatusLabel status={status as InspectionStatus} />
                </Table.Cell>
                <Table.Cell textAlign='right'>
                  <InspectionScheduledCell
                    status={status as InspectionStatus}
                    scheduledDateTime={scheduledAt}
                    scheduledDate={scheduledDate}
                    scheduledMeridiem={scheduledMeridiem}
                    preferences={preferences}
                    onRequestInspection={(ev) => {
                      ev.stopPropagation();
                      onRequestModalOpen(inspection.id);
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </Segment>
  );
};

interface InspectionTitleCellProps {
  title: string;
  isReInspection: boolean;
}
function InspectionTitleCell({ title, isReInspection }: InspectionTitleCellProps) {
  return (
    <div>
      {title}
      {isReInspection && (
        <div className='subText'>
          {" "}
          <FontAwesomeIcon className='tw-mr-1' icon={faRotateRight} />
          Reinspection
        </div>
      )}
    </div>
  );
}

interface IInspectionScheduledCell {
  status: InspectionStatus;
  scheduledDateTime: string | null;
  scheduledDate: string | null;
  scheduledMeridiem: string | null;
  preferences: { date: string | null; meridiem: string | null }[];
  onRequestInspection: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}
function InspectionScheduledCell({
  status,
  scheduledDateTime,
  scheduledDate,
  scheduledMeridiem,
  preferences,
  onRequestInspection,
}: IInspectionScheduledCell) {
  if (status === InspectionStatuses.REQUESTED && preferences.length !== 0) {
    const firstRequestPreference = preferences[0];
    const firstRequestedDay = firstRequestPreference.date ?? "";
    const firstRequestedMeridiem = firstRequestPreference.meridiem ?? "";

    return (
      <>
        <p className='tw-mb-0 tw-italic'>
          Requested{" "}
          {new Date(firstRequestedDay).toLocaleDateString(undefined, {
            month: "short",
            year: "numeric",
            day: "numeric",
          })}{" "}
          {firstRequestedMeridiem}
        </p>
        <Button
          onClick={onRequestInspection}
          className='tw-mt-1 tw-border-none tw-bg-transparent tw-px-0 tw-py-1 tw-text-blue focus:tw-ring-2 focus:tw-ring-blue focus:tw-ring-offset-2'
        >
          Change Requested Date
        </Button>
      </>
    );
  }

  if (status === InspectionStatuses.UNSCHEDULED) {
    return (
      <Button primary onClick={onRequestInspection}>
        Request Inspection
      </Button>
    );
  }

  return (
    <InspectionScheduledDateDisplay
      scheduledDate={scheduledDate}
      scheduledDateTime={scheduledDateTime}
      scheduledMeridiem={scheduledMeridiem}
    />
  );
}
